/**
 * StreakBoard - the daily Streak Board we post to Instagram.
 *
 * Today's 3-4 streaks that historically keep going, the previous board's
 * results, and the running record (GET /api/streaks/board).
 */
import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const headshotUrl = (sport, espnId) =>
  espnId ? `https://a.espncdn.com/i/headshots/${sport.toLowerCase()}/players/full/${espnId}.png` : null;

const logoUrl = (sport, team) =>
  `https://a.espncdn.com/i/teamlogos/${sport.toLowerCase()}/500/${team.toLowerCase()}.png`;

function gameTime(time) {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'} ET`;
}

function Headshot({ sport, espnId, team, ring }) {
  const src = headshotUrl(sport, espnId);
  return (
    <div className="relative shrink-0">
      <div className={`w-16 h-16 rounded-full overflow-hidden bg-bg-secondary ring-2 ${ring}`}>
        {src && <img src={src} alt="" className="w-full h-full object-cover" />}
      </div>
      <img src={logoUrl(sport, team)} alt="" className="absolute -bottom-1 -right-1 w-7 h-7" />
    </div>
  );
}

function BoardCard({ s }) {
  const graded = s.hit === '1' || s.hit === '0';
  const where = s.home === null ? 'vs' : s.home ? 'vs' : '@';
  return (
    <div className="glass-card p-4 border-l-4 border-l-green-500">
      <div className="flex items-start gap-4">
        <Headshot sport={s.sport} espnId={s.espn_id} team={s.team} ring="ring-green-500/60" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-text-primary truncate">{s.player}</h3>
              <p className="text-xs text-text-secondary">
                {s.sport} · {s.team} {where} {s.opponent}{s.time ? ` · ${gameTime(s.time)}` : ''}
              </p>
            </div>
            <div className="text-right shrink-0">
              <div className="text-2xl font-extrabold text-green-400 leading-none">{Math.round(s.rate * 100)}%</div>
              <div className="text-[10px] text-text-secondary mt-1">keep going<br />historically</div>
            </div>
          </div>
          <p className="mt-2 text-sm font-bold text-green-400 uppercase tracking-wide">
            {s.threshold}+ {s.label} in {s.run} straight
          </p>
          {s.last10 && (
            <div className="mt-2 flex items-center gap-1 flex-wrap">
              <span className="text-[10px] font-semibold text-text-secondary mr-1">
                {s.last10.length === 10 ? 'LAST 10' : 'SEASON'}
              </span>
              {s.last10.map((v, i) => (
                <span
                  key={i}
                  className={`w-7 h-6 flex items-center justify-center rounded text-xs font-bold ${
                    v >= s.threshold ? 'bg-green-500 text-bg-primary' : 'bg-bg-secondary text-text-primary'
                  }`}
                >
                  {v}
                </span>
              ))}
            </div>
          )}
          {graded && (
            <p className={`mt-2 text-sm font-semibold ${s.hit === '1' ? 'text-green-400' : 'text-red-400'}`}>
              {s.hit === '1' ? 'Hit' : 'Missed'}: {Math.round(Number(s.actual))}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultRow({ s }) {
  const hit = s.hit === '1';
  const voided = s.hit === 'void';
  const pending = !hit && !voided && s.hit !== '0';
  const color = hit ? 'text-green-400' : voided || pending ? 'text-text-secondary' : 'text-red-400';
  return (
    <div className="flex items-center gap-3 py-2">
      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
        hit ? 'bg-green-500 text-bg-primary' : voided || pending ? 'bg-bg-secondary text-text-secondary' : 'bg-red-500 text-white'
      }`}>
        {hit ? '✓' : voided ? '–' : pending ? '…' : '✕'}
      </span>
      <span className="flex-1 text-sm text-text-primary truncate">
        {s.player} <span className="text-text-secondary">· {s.threshold}+ {s.label.toLowerCase()}</span>
      </span>
      <span className={`text-sm font-bold ${color}`}>
        {voided ? 'DNP' : pending ? 'pending' : Math.round(Number(s.actual))}
      </span>
    </div>
  );
}

export default function StreakBoard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get('/streaks/board')
      .then(res => setData(res.data))
      .catch(err => {
        console.error('Error fetching streak board:', err);
        setError(true);
      });
  }, []);

  if (error || (data && !data.board?.length)) return null;
  if (!data) {
    return <div className="glass-card p-6 mb-6 animate-pulse h-48" />;
  }

  const day = new Date(`${data.date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const prev = data.previous;
  const prevDay = prev && new Date(`${prev.date}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="mb-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-4">
        <div>
          <h2 className="text-2xl font-extrabold text-text-primary tracking-tight">Streak Board</h2>
          <p className="text-sm text-text-secondary">{day} · streaks that historically keep going</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="px-3 py-1 rounded-full bg-green-500/15 text-green-400 text-sm font-semibold">{data.record}</span>
          {data.instagram && (
            <a href={data.instagram} target="_blank" rel="noopener noreferrer"
               className="text-sm font-semibold text-accent-blue hover:underline">
              Instagram
            </a>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.board.map(s => <BoardCard key={`${s.player}-${s.stat}`} s={s} />)}
      </div>

      {prev && (
        <div className="glass-card p-4 mt-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-text-secondary">{prevDay} board</span>
            {prev.graded > 0 && (
              <span className={`text-sm font-extrabold ${prev.hits === prev.graded ? 'text-green-400' : 'text-text-primary'}`}>
                {prev.hits} for {prev.graded}
              </span>
            )}
          </div>
          <div className="divide-y divide-white/5">
            {prev.board.map(s => <ResultRow key={`${s.player}-${s.stat}`} s={s} />)}
          </div>
        </div>
      )}

      <p className="text-[11px] text-text-secondary mt-3">
        Rate = how often streaks like this continued the next game (NBA &amp; NHL, 2024-26). This season's games only.
        For entertainment. 21+. Gamble responsibly.
      </p>
    </div>
  );
}
